<template>
  <div>
    <div class="mb-2">
      <label v-if="title" class="text-sm font-semibold text-gray-700 mb-1 block" for="signature_pad">
        {{ title }}
      </label>

      <div v-if="filename">
        <div
          v-if="show_signature_if_filename"
          class="flex justify-center items-center border border-gray-300 rounded"
          :style="{ height: normalizedHeight }"
        >
          <img
            class="m-5 max-w-full max-h-full object-contain"
            :src="signatureImageUrl"
            alt="Signature"
          />
        </div>

        <div class="mt-2" v-if="can_redo && !view_only">
          <button
            type="button"
            class="inline-flex items-center px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
            @click="redo_signature"
          >
            <span class="mr-2">↻</span> Redo <span v-if="!show_signature_if_filename">&nbsp;Signature</span>
          </button>
        </div>
      </div>

      <div v-else>
        <div
          v-if="show_no_signature_message"
          class="text-center text-sm bg-red-50 border border-red-200 text-red-700 rounded p-2 mb-2"
        >
          <div class="font-semibold">No signature detected.</div>
          Please sign in the signature box below.
        </div>

        <div
          v-if="view_only"
          class="text-center text-sm bg-gray-50 border border-gray-200 text-gray-600 rounded p-3"
        >
          Signature not available.
        </div>

        <div v-else>
          <VueSignaturePad
            id="signature_pad"
            :width="normalizedWidth"
            :height="normalizedHeight"
            ref="signature_pad"
            :options="{ onBegin: handleBegin }"
            class="signature-border"
          />

          <div class="mt-2 flex items-center justify-between">
            <div class="flex gap-2">
              <button
                type="button"
                class="inline-flex items-center px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                @click="clear_signature_pad"
              >
                <span class="mr-2">⌫</span> Clear
              </button>

              <button
                type="button"
                class="inline-flex items-center px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                @click="undo_signature_pad"
              >
                <span class="mr-2">↶</span> Undo
              </button>
            </div>

            <button
              type="button"
              class="inline-flex items-center px-3 py-1.5 text-sm border rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              :disabled="saving"
              @click="submit_signature_pad"
            >
              <span class="mr-2">✓</span> {{ saving ? 'Saving…' : 'Confirm' }}
            </button>
          </div>

          <div v-if="show_signature_approved_message" class="mt-2 text-sm text-green-700">
            Signature saved.
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="alert_modal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="alert_modal = false"
    >
      <div class="bg-white w-full max-w-md rounded shadow p-4">
        <div class="text-sm text-gray-800" v-html="alert_message"></div>

        <div class="mt-4 flex justify-end">
          <button
            type="button"
            class="inline-flex items-center px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
            @click="alert_modal = false"
          >
            Okay
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { VueSignaturePad } from 'vue-signature-pad'

export default {
  name: 'SignaturePad',
  emits: ['signature'],

  components: {
    VueSignaturePad,
  },

  props: {
    title: { type: String, default: null },
    view_only: { type: Boolean, default: false },
    can_redo: { type: Boolean, default: true },
    width: { type: [String, Number], default: '100%' },
    height: { type: [String, Number], default: 200 },
    token: { type: String, default: null },
    test_attempt_id: { type: Number, default: null },
    filename: { type: String, default: null },
    show_signature_if_filename: { type: Boolean, default: true },
    object: { type: Object, default: null },
    object_property_name_filename: { type: String, default: null },
    object_property_name_date: { type: String, default: null },
    type: { type: String, required: true },
  },

  data() {
    return {
      show_no_signature_message: false,
      show_signature_approved_message: false,
      alert_modal: false,
      alert_message: null,
      saving: false,
    }
  },

  computed: {
    normalizedWidth() {
      return typeof this.width === 'number' ? `${this.width}px` : this.width
    },
    normalizedHeight() {
      return typeof this.height === 'number' ? `${this.height}px` : this.height
    },

    url_parameters() {
      const params = new URLSearchParams()
      if (this.token) params.set('token', this.token)
      if (this.test_attempt_id) params.set('test_attempt_id', String(this.test_attempt_id))
      const qs = params.toString()
      return qs ? `&${qs}` : ''
    },

    signatureImageUrl() {
      const encoded = encodeURIComponent(this.filename || '')
      return this.filename
        ? `/get_${this.type}_signature?filename=${encoded}${this.url_parameters}`
        : ''
    },
  },

  methods: {
    handleBegin() {
      this.$refs.signature_pad?.resizeCanvas?.()
    },

    async submit_signature_pad() {
      if (this.saving) return

      const pad = this.$refs.signature_pad
      if (!pad || !pad.saveSignature) return

      const { isEmpty } = pad.saveSignature()
      if (isEmpty) {
        this.show_no_signature_message = true
        setTimeout(() => (this.show_no_signature_message = false), 5000)
        return
      }

      const canvas = pad.$el?.querySelector?.('canvas')
      if (!canvas) {
        this.alert_message =
          'Signature pad did not produce a <canvas>. This usually means VueSignaturePad was not registered or failed to mount.'
        this.alert_modal = true
        return
      }

      const croppedDataUrl = this.crop_signature_canvas(canvas)
      if (!croppedDataUrl) {
        this.show_no_signature_message = true
        setTimeout(() => (this.show_no_signature_message = false), 5000)
        return
      }

      const payload = { data: croppedDataUrl }
      if (this.token) payload.token = this.token
      if (this.test_attempt_id) payload.test_attempt_id = this.test_attempt_id

      this.saving = true
      this.show_signature_approved_message = false

      try {
        const res = await axios.post(`/${this.type}_signature_store`, payload)

        if (res?.data?.result === 'success') {
          if (this.object && this.object_property_name_filename) {
            this.object[this.object_property_name_filename] = res.data.filename
          }
          if (this.object && this.object_property_name_date) {
            this.object[this.object_property_name_date] = res.data.last_updated
          }

          this.show_signature_approved_message = true
          this.$emit('signature', res.data.filename)
        } else {
          this.alert_message = res?.data?.msg ?? 'Unable to save signature.'
          this.alert_modal = true
        }
      } catch (e) {
        this.alert_message =
          e?.response?.data?.message ??
          e?.message ??
          'An error occurred while saving the signature.'
        this.alert_modal = true
      } finally {
        this.saving = false
      }
    },

    clear_signature_pad() {
      this.$refs.signature_pad?.clearSignature?.()
    },

    undo_signature_pad() {
      this.$refs.signature_pad?.undoSignature?.()
    },

    redo_signature() {
      if (this.object && this.object_property_name_filename) {
        this.object[this.object_property_name_filename] = null
      }
      if (this.object && this.object_property_name_date) {
        this.object[this.object_property_name_date] = null
      }

      this.$nextTick(() => {
        this.undo_signature_pad()
        this.show_no_signature_message = false
        this.show_signature_approved_message = false
      })
    },

    crop_signature_canvas(canvas) {
      const croppedCanvas = document.createElement('canvas')
      const croppedCtx = croppedCanvas.getContext('2d')
      if (!croppedCtx) return null

      croppedCanvas.width = canvas.width
      croppedCanvas.height = canvas.height
      croppedCtx.drawImage(canvas, 0, 0)

      const w = croppedCanvas.width
      const h = croppedCanvas.height
      const imageData = croppedCtx.getImageData(0, 0, w, h)

      const pix = { x: [], y: [] }

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const index = (y * w + x) * 4
          if (imageData.data[index + 3] > 0) {
            pix.x.push(x)
            pix.y.push(y)
          }
        }
      }

      if (!pix.x.length || !pix.y.length) return null

      pix.x.sort((a, b) => a - b)
      pix.y.sort((a, b) => a - b)

      const minX = pix.x[0]
      const maxX = pix.x[pix.x.length - 1]
      const minY = pix.y[0]
      const maxY = pix.y[pix.y.length - 1]

      const pad = 6
      const cropX = Math.max(minX - pad, 0)
      const cropY = Math.max(minY - pad, 0)
      const cropW = Math.min(maxX - minX + pad * 2, w - cropX)
      const cropH = Math.min(maxY - minY + pad * 2, h - cropY)

      const cut = croppedCtx.getImageData(cropX, cropY, cropW, cropH)

      croppedCanvas.width = cropW
      croppedCanvas.height = cropH
      croppedCtx.putImageData(cut, 0, 0)

      return croppedCanvas.toDataURL('image/png')
    },
  },
}
</script>

<style scoped>
.signature-border {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  display: block;
  width: 100%;
}
</style>
